import { io, Socket } from 'socket.io-client';
import { AdminApi, TokenStorage } from './api';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3000';

type Gestionnaire = (...args: unknown[]) => void;

/**
 * Connexion temps réel du personnel.
 *
 * - Le jeton est relu à chaque (re)connexion : l'option `auth` est une
 *   fonction, un jeton rafraîchi entre-temps est donc bien présenté.
 * - Chaque gestionnaire est posé une seule fois sur le socket (à sa création
 *   ou à l'abonnement) : une reconnexion ne les duplique pas.
 * - Les salles de dossier demandées sont rejointes de nouveau à chaque
 *   connexion (l'événement `connect` revient après chaque reconnexion).
 * - Jeton expiré pendant une coupure (veille, réseau) : le serveur refuse la
 *   reconnexion (AUTH_TOKEN_EXPIRED). Le jeton est alors rafraîchi, puis la
 *   connexion reprend.
 * - Les événements émis pendant une coupure sont perdus : à chaque
 *   reconnexion (pas à la première connexion), les écrans abonnés par
 *   `surReconnexion` relisent ce qu'ils affichent.
 */
class AdminSocketService {
  /** Au plus un rafraîchissement de jeton par période (évite toute boucle). */
  static readonly INTERVALLE_RAFRAICHISSEMENT_MS = 30_000;

  private socket: Socket | null = null;
  private listeners: Map<string, Set<Gestionnaire>> = new Map();
  private pendingRooms: Set<string> = new Set();
  private relance: ReturnType<typeof setTimeout> | null = null;
  private reconnexions: Set<() => void> = new Set();
  private dejaConnecte = false;
  private rafraichissementEnCours = false;
  private dernierRafraichissement = 0;

  /** `rafraichir` renouvelle le jeton d'accès ; false si la session est perdue. */
  constructor(private readonly rafraichir: () => Promise<boolean> = () => AdminApi.rafraichirSession()) {}

  connect(): void {
    if (!TokenStorage.getAccessToken()) return;
    if (this.socket) {
      // Déjà créé : relancer s'il est arrêté (refus du serveur, déconnexion).
      if (!this.socket.connected && !this.socket.active) this.socket.connect();
      return;
    }

    const socket = io(SOCKET_URL, {
      auth: (cb) => cb({ token: TokenStorage.getAccessToken() ?? '' }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });
    this.socket = socket;

    // Gestionnaires des écrans, posés une fois.
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => socket.on(event, callback));
    });

    socket.on('connect', () => {
      this.pendingRooms.forEach((ticketId) => socket.emit('join:ticket', { ticketId }));
      socket.emit('subscribe:notifications');
      if (this.dejaConnecte) {
        // Reconnexion : ce qui a été émis pendant la coupure est perdu.
        this.reconnexions.forEach((rappel) => {
          try {
            rappel();
          } catch (e) {
            console.error('[AdminSocket] Resynchronisation :', e);
          }
        });
      }
      this.dejaConnecte = true;
    });

    socket.on('connect_error', (error) => {
      console.warn('[AdminSocket] Connexion refusée :', error.message);
      // Jeton expiré : on le rafraîchit, puis on reconnecte.
      if (error.message === 'AUTH_TOKEN_EXPIRED' && this.rafraichirPuisReconnecter()) return;
      // Un autre refus du serveur arrête les tentatives automatiques :
      // on réessaie plus tard, avec le jeton du moment.
      if (!socket.active) this.programmerRelance();
    });

    socket.on('disconnect', (reason) => {
      // Déconnexion décidée par le serveur : pas de reconnexion automatique.
      if (reason === 'io server disconnect') this.programmerRelance();
    });
  }

  private programmerRelance(): void {
    if (this.relance !== null) return;
    this.relance = setTimeout(() => {
      this.relance = null;
      if (this.socket && !this.socket.connected && TokenStorage.getAccessToken()) this.socket.connect();
    }, 5000);
  }

  /**
   * Jeton expiré : rafraîchit puis reconnecte. Si un rafraîchissement a déjà
   * eu lieu dans la période, rend false (relance ordinaire) : jamais de
   * boucle si le serveur refuse encore le jeton neuf. Si la session est
   * perdue (rafraîchissement refusé), on n'insiste pas.
   */
  private rafraichirPuisReconnecter(): boolean {
    if (this.rafraichissementEnCours) return true;
    if (Date.now() - this.dernierRafraichissement < AdminSocketService.INTERVALLE_RAFRAICHISSEMENT_MS) return false;
    this.rafraichissementEnCours = true;
    this.dernierRafraichissement = Date.now();
    this.rafraichir()
      .catch(() => false)
      .then((ok) => {
        this.rafraichissementEnCours = false;
        const socket = this.socket;
        if (!socket || socket.connected || socket.active) return;
        if (!ok || !TokenStorage.getAccessToken()) return;
        socket.connect();
      });
    return true;
  }

  /**
   * Rappel exécuté à chaque reconnexion (pas à la première connexion) : les
   * écrans y relisent leurs données. Rend la fonction de désabonnement.
   */
  surReconnexion(rappel: () => void): () => void {
    this.reconnexions.add(rappel);
    return () => {
      this.reconnexions.delete(rappel);
    };
  }

  /** Reprend la connexion avec le jeton courant (après un changement de jeton). */
  reconnecter(): void {
    if (!this.socket) {
      this.connect();
      return;
    }
    this.socket.disconnect();
    this.socket.connect();
  }

  disconnect(): void {
    if (this.relance !== null) clearTimeout(this.relance);
    this.relance = null;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.pendingRooms.clear();
    this.dejaConnecte = false;
    this.rafraichissementEnCours = false;
    this.dernierRafraichissement = 0;
  }

  on(event: string, callback: Gestionnaire): void {
    let callbacks = this.listeners.get(event);
    if (!callbacks) {
      callbacks = new Set();
      this.listeners.set(event, callbacks);
    }
    if (callbacks.has(callback)) return;
    callbacks.add(callback);
    this.socket?.on(event, callback);
  }

  off(event: string, callback: Gestionnaire): void {
    this.listeners.get(event)?.delete(callback);
    this.socket?.off(event, callback);
  }

  emit(event: string, data?: unknown): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  joinTicketRoom(ticketId: string): void {
    this.pendingRooms.add(ticketId);
    if (this.socket?.connected) this.socket.emit('join:ticket', { ticketId });
  }

  leaveTicketRoom(ticketId: string): void {
    this.pendingRooms.delete(ticketId);
    if (this.socket?.connected) this.socket.emit('leave:ticket', { ticketId });
  }

  subscribeToAdminNotifications(): void {
    this.emit('subscribe:notifications');
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export { AdminSocketService };
export const adminSocketService = new AdminSocketService();
export default adminSocketService;
